import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../../context/AppContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { 
  Youtube, 
  Video, 
  MessageSquare, 
  Users, 
  Settings, 
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  Clock,
  Eye,
  TrendingUp,
  Image
} from 'lucide-react';

const Dashboard = () => {
  const { user, API, settings, fetchSettings } = useApp();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    videos: 0,
    messages: 0,
    users: 0
  });
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [needsSetup, setNeedsSetup] = useState(false);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [videosRes, messagesRes, setupRes, analyticsRes] = await Promise.all([
          axios.get(`${API}/admin/youtube/videos`, { withCredentials: true }),
          axios.get(`${API}/admin/messages`, { withCredentials: true }),
          axios.get(`${API}/admin/check-setup`),
          axios.get(`${API}/admin/analytics?days=7`, { withCredentials: true }).catch(() => ({ data: null }))
        ]);
        
        setStats({
          videos: videosRes.data.videos?.length || 0,
          messages: messagesRes.data.messages?.filter(m => !m.read).length || 0
        });
        
        setAnalytics(analyticsRes.data);
        setNeedsSetup(!setupRes.data.needs_admin && setupRes.data.needs_setup);
        
        if (user?.role === 'admin') {
          const usersRes = await axios.get(`${API}/admin/users`, { withCredentials: true });
          setStats(prev => ({ ...prev, users: usersRes.data.users?.length || 0 }));
        }
      } catch (error) {
        console.error('Failed to fetch stats:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchStats();
    fetchSettings();
  }, [API, user, fetchSettings]);

  // Determine YouTube configuration status
  const hasYouTubeCredentials = settings?.youtube_api_key && settings?.youtube_channel_id;
  const hasSyncedVideos = stats.videos > 0;
  const lastSyncStatus = settings?.last_sync_status;
  const lastSyncError = settings?.last_sync_error;
  
  // YouTube is "configured" if we have credentials OR if we have synced videos
  const youtubeConfigured = hasYouTubeCredentials || hasSyncedVideos;

  const getYouTubeStatusColor = () => {
    if (!youtubeConfigured) return 'text-brand-gold';
    if (lastSyncStatus === 'error') return 'text-red-500';
    return 'text-green-500';
  };

  const getYouTubeStatusText = () => {
    if (!youtubeConfigured) return 'Not Configured';
    if (lastSyncStatus === 'error') return 'Sync Error';
    if (hasSyncedVideos) return 'Connected';
    return 'Configured';
  };

  const getYouTubeStatusIcon = () => {
    if (!youtubeConfigured) return <AlertTriangle className="w-5 h-5" />;
    if (lastSyncStatus === 'error') return <AlertTriangle className="w-5 h-5" />;
    return <CheckCircle className="w-5 h-5" />;
  };

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      {/* Setup Wizard Banner */}
      {needsSetup && (
        <Card className="bg-brand-gold/10 border-brand-gold/30">
          <CardContent className="flex items-center justify-between p-6">
            <div className="flex items-center gap-4">
              <AlertTriangle className="w-8 h-8 text-brand-gold" />
              <div>
                <h3 className="font-syne font-semibold text-white">Setup Required</h3>
                <p className="text-white/60 text-sm">Complete the initial setup to get your website running.</p>
              </div>
            </div>
            <Button 
              onClick={() => navigate('/admin/settings')}
              className="bg-brand-gold hover:bg-brand-gold-light"
              data-testid="setup-wizard-btn"
            >
              Start Setup
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Welcome */}
      <div>
        <h1 className="font-syne font-bold text-2xl md:text-3xl text-white mb-2">
          Welcome back, <span className="text-brand-gold">{user?.username}</span>
        </h1>
        <p className="text-white/50">Here's an overview of your music website.</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-brand-dark border-white/5 hover:border-brand-gold/30 transition-colors">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-white/50 font-manrope font-medium flex items-center gap-2">
              <Video className="w-4 h-4 text-brand-gold" />
              Cached Videos
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-syne font-bold text-white">
              {loading ? '-' : stats.videos}
            </p>
          </CardContent>
        </Card>

        <Card className="bg-brand-dark border-white/5 hover:border-brand-gold/30 transition-colors">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-white/50 font-manrope font-medium flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-brand-gold" />
              Unread Messages
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-syne font-bold text-white">
              {loading ? '-' : stats.messages}
            </p>
          </CardContent>
        </Card>

        {user?.role === 'admin' && (
          <Card className="bg-brand-dark border-white/5 hover:border-brand-gold/30 transition-colors">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-white/50 font-manrope font-medium flex items-center gap-2">
                <Users className="w-4 h-4 text-brand-gold" />
                Admin Users
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-syne font-bold text-white">
                {loading ? '-' : stats.users}
              </p>
            </CardContent>
          </Card>
        )}

        <Card className="bg-brand-dark border-white/5 hover:border-brand-gold/30 transition-colors">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-white/50 font-manrope font-medium flex items-center gap-2">
              <Eye className="w-4 h-4 text-brand-gold" />
              Views (7 days)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-syne font-bold text-white">
              {loading ? '-' : (analytics?.total_views || 0)}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* YouTube Status */}
        <Card className="bg-brand-dark border-white/5">
          <CardHeader>
            <CardTitle className="font-syne text-white flex items-center gap-2">
              <Youtube className="w-5 h-5 text-brand-red" />
              YouTube Integration
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Status Display */}
            <div className="flex items-center gap-3 p-3 bg-brand-charcoal rounded-lg">
              <div className={getYouTubeStatusColor()}>
                {getYouTubeStatusIcon()}
              </div>
              <div className="flex-1">
                <p className={`font-medium ${getYouTubeStatusColor()}`}>
                  {getYouTubeStatusText()}
                </p>
                {settings?.last_sync_at && (
                  <p className="text-white/40 text-xs">
                    Last sync: {new Date(settings.last_sync_at).toLocaleString()}
                    {settings.last_sync_count !== undefined && (
                      <span> ({settings.last_sync_count} videos)</span>
                    )}
                  </p>
                )}
                {lastSyncError && (
                  <p className="text-red-400 text-xs mt-1">{lastSyncError}</p>
                )}
              </div>
            </div>

            {/* Action Button */}
            {youtubeConfigured ? (
              <Button 
                onClick={() => navigate('/admin/youtube')}
                className="w-full bg-brand-red hover:bg-brand-red-light"
                data-testid="sync-youtube-btn"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Manage & Sync
              </Button>
            ) : (
              <Button 
                onClick={() => navigate('/admin/settings')}
                variant="outline"
                className="w-full border-white/10 text-white hover:bg-white/5"
              >
                <Settings className="w-4 h-4 mr-2" />
                Configure API
              </Button>
            )}

            <p className="text-white/30 text-xs text-center">
              Auto-sync runs daily at 3:00 AM
            </p>
          </CardContent>
        </Card>

        {/* Recent Messages */}
        <Card className="bg-brand-dark border-white/5">
          <CardHeader>
            <CardTitle className="font-syne text-white flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-brand-gold" />
              Messages
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-white/50 text-sm">
              {stats.messages > 0 
                ? `You have ${stats.messages} unread message${stats.messages > 1 ? 's' : ''}.`
                : 'No new messages.'}
            </p>
            <Button 
              onClick={() => navigate('/admin/messages')}
              variant="outline"
              className="w-full border-white/10 text-white hover:bg-white/5 hover:border-brand-gold/50"
            >
              View Messages
            </Button>
          </CardContent>
        </Card>

        {/* Quick Links */}
        <Card className="bg-brand-dark border-white/5">
          <CardHeader>
            <CardTitle className="font-syne text-white flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-brand-red" />
              Quick Links
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <Button 
              onClick={() => navigate('/admin/content')}
              variant="ghost"
              className="w-full justify-start text-white/70 hover:text-white hover:bg-white/5"
            >
              Edit Content & Texts
            </Button>
            <Button 
              onClick={() => navigate('/admin/translations')}
              variant="ghost"
              className="w-full justify-start text-white/70 hover:text-white hover:bg-white/5"
            >
              Manage Translations
            </Button>
            <Button 
              onClick={() => navigate('/admin/videos')}
              variant="ghost"
              className="w-full justify-start text-white/70 hover:text-white hover:bg-white/5"
            >
              Video Overrides
            </Button>
            <Button 
              onClick={() => navigate('/admin/media')}
              variant="ghost"
              className="w-full justify-start text-white/70 hover:text-white hover:bg-white/5"
            >
              <Image className="w-4 h-4 mr-2" />
              Media Manager
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Analytics Preview */}
      {analytics && analytics.top_pages?.length > 0 && (
        <Card className="bg-brand-dark border-white/5">
          <CardHeader>
            <CardTitle className="font-syne text-white flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-brand-gold" />
              Top Pages (Last 7 Days)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {analytics.top_pages.slice(0, 5).map((page, index) => (
                <div key={page.route} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-brand-gold font-mono text-sm">#{index + 1}</span>
                    <span className="text-white/70">{page.route}</span>
                  </div>
                  <span className="text-white font-medium">{page.views} views</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Dashboard;
